import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Checkbox,
  CircularProgress,
  Divider,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { usePlans, useAssignSubscription } from '../../hooks/subscriptions';
import { formatPrice, formatBillingInterval } from '../../utils/subscriptions';

interface OrganizationFormData {
  name: string;
  slug: string;
  contactEmail: string;
  billingEmail: string;
  phone: string;
  address: string;
  // Owner fields
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerPassword: string;
  confirmPassword: string;
}

// Password validation helper
const getPasswordStrengthHelperText = (password: string): string => {
  if (!password) {
    return 'Password must be at least 8 characters with uppercase, lowercase, number, and special character (@$!%*?&)';
  }

  const requirements: string[] = [];

  if (password.length < 8) {
    requirements.push('at least 8 characters');
  }
  if (!/[A-Z]/.test(password)) {
    requirements.push('uppercase letter');
  }
  if (!/[a-z]/.test(password)) {
    requirements.push('lowercase letter');
  }
  if (!/\d/.test(password)) {
    requirements.push('number');
  }
  if (!/[@$!%*?&]/.test(password)) {
    requirements.push('special character (@$!%*?&)');
  }

  if (requirements.length === 0) {
    return 'Password meets all requirements';
  }

  return `Missing: ${requirements.join(', ')}`;
};

// Password validation regex matching backend
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

// Phone validation regex (E.164 format)
const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/;

export const OrganizationCreate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [createOwner, setCreateOwner] = useState(true);

  // Plan selection state
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(14);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<OrganizationFormData>();

  // Watch the name field for slug auto-generation
  const orgName = watch('name');

  // Auto-generate slug from organization name
  useEffect(() => {
    if (orgName) {
      const generatedSlug = orgName
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
      setValue('slug', generatedSlug);
    }
  }, [orgName, setValue]);

  // Fetch available plans
  const { data: plans, isLoading: plansLoading } = usePlans();
  const assignMutation = useAssignSubscription();

  const selectedPlan = plans?.find((p) => p.id === selectedPlanId);

  const ownerPassword = watch('ownerPassword');

  const createMutation = useMutation({
    mutationFn: (data: any) => axiosInstance.post('/organizations', data),
    onSuccess: (response) => {
      const newOrgId = response.data.id;

      // If a plan was selected, assign it to the new organization
      if (selectedPlanId) {
        assignMutation.mutate(
          {
            organizationId: newOrgId,
            planId: selectedPlanId,
            ...(isTrial && { isTrial: true, trialDays }),
          },
          {
            onSuccess: () => {
              const successMessage = isTrial
                ? `Organization created with ${trialDays}-day trial`
                : 'Organization created with subscription';
              showMessage(successMessage, 'success');
              navigate('/organizations');
            },
            onError: (error: any) => {
              // Org was created but plan assignment failed
              showMessage(
                `Organization created but failed to assign plan: ${error.response?.data?.message || 'Unknown error'}`,
                'warning'
              );
              navigate('/organizations');
            },
          }
        );
      } else {
        showMessage('Organization created successfully', 'success');
        navigate('/organizations');
      }
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to create organization', 'error');
    },
  });

  // Calculate trial expiration date for display
  const trialExpirationDate = new Date();
  trialExpirationDate.setDate(trialExpirationDate.getDate() + trialDays);

  const onSubmit = (data: OrganizationFormData) => {
    // Build the API payload
    const payload: {
      name: string;
      slug: string;
      contactEmail: string;
      billingEmail: string;
      phone?: string;
      address?: string;
      owner?: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        password: string;
      };
    } = {
      name: data.name,
      slug: data.slug,
      contactEmail: data.contactEmail,
      billingEmail: data.billingEmail,
    };

    // Include optional organization fields if provided
    if (data.phone) {
      payload.phone = data.phone;
    }
    if (data.address) {
      payload.address = data.address;
    }

    // Include owner data only when checkbox is checked
    if (createOwner) {
      payload.owner = {
        firstName: data.ownerFirstName,
        lastName: data.ownerLastName,
        email: data.ownerEmail,
        phone: data.ownerPhone,
        password: data.ownerPassword,
        // NOTE: confirmPassword is intentionally excluded from API payload
      };
    }

    createMutation.mutate(payload);
  };

  const isPending = createMutation.isPending || assignMutation.isPending;

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Create Organization
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Organization Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Organization Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Organization name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Organization Name"
                  autoFocus
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('slug', {
                    required: 'Slug is required',
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug must contain only lowercase letters, numbers, and hyphens',
                    },
                  })}
                  error={!!errors.slug}
                  helperText={errors.slug?.message || 'Unique identifier (e.g., my-organization)'}
                  fullWidth
                  label="Slug"
                  placeholder="e.g., my-organization"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('contactEmail', {
                    required: 'Contact email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.contactEmail}
                  helperText={errors.contactEmail?.message}
                  fullWidth
                  label="Contact Email"
                  type="email"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('billingEmail', {
                    required: 'Billing email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  error={!!errors.billingEmail}
                  helperText={errors.billingEmail?.message}
                  fullWidth
                  label="Billing Email"
                  type="email"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  fullWidth
                  label="Phone"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('address')}
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Owner Creation Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={createOwner}
                      onChange={(e) => setCreateOwner(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="h6">Create Owner User</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create an organization owner with full administrative access
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

              {createOwner && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                      Owner Details
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('ownerFirstName', {
                        required: createOwner ? 'First name is required' : false,
                        minLength: {
                          value: 1,
                          message: 'First name must not be empty',
                        },
                        maxLength: {
                          value: 50,
                          message: 'First name must not exceed 50 characters',
                        },
                      })}
                      error={!!errors.ownerFirstName}
                      helperText={errors.ownerFirstName?.message}
                      fullWidth
                      label="First Name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('ownerLastName', {
                        required: createOwner ? 'Last name is required' : false,
                        minLength: {
                          value: 1,
                          message: 'Last name must not be empty',
                        },
                        maxLength: {
                          value: 50,
                          message: 'Last name must not exceed 50 characters',
                        },
                      })}
                      error={!!errors.ownerLastName}
                      helperText={errors.ownerLastName?.message}
                      fullWidth
                      label="Last Name"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('ownerEmail', {
                        required: createOwner ? 'Owner email is required' : false,
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                        maxLength: {
                          value: 255,
                          message: 'Email must not exceed 255 characters',
                        },
                      })}
                      error={!!errors.ownerEmail}
                      helperText={errors.ownerEmail?.message}
                      fullWidth
                      label="Owner Email"
                      type="email"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('ownerPhone', {
                        required: createOwner ? 'Phone number is required' : false,
                        pattern: {
                          value: PHONE_REGEX,
                          message: 'Phone must be in E.164 format (e.g., +213555123456)',
                        },
                        maxLength: {
                          value: 20,
                          message: 'Phone number must not exceed 20 characters',
                        },
                      })}
                      error={!!errors.ownerPhone}
                      helperText={errors.ownerPhone?.message || 'E.164 format (e.g., +213555123456)'}
                      fullWidth
                      label="Owner Phone"
                      placeholder="+213555123456"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('ownerPassword', {
                        required: createOwner ? 'Password is required' : false,
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                        maxLength: {
                          value: 128,
                          message: 'Password must not exceed 128 characters',
                        },
                        pattern: {
                          value: PASSWORD_REGEX,
                          message: 'Password must contain uppercase, lowercase, number, and special character',
                        },
                      })}
                      error={!!errors.ownerPassword}
                      helperText={
                        errors.ownerPassword?.message ||
                        getPasswordStrengthHelperText(ownerPassword || '')
                      }
                      fullWidth
                      label="Password"
                      type="password"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      {...register('confirmPassword', {
                        required: createOwner ? 'Please confirm password' : false,
                        validate: (value) =>
                          !createOwner || value === ownerPassword || 'Passwords do not match',
                      })}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword?.message}
                      fullWidth
                      label="Confirm Password"
                      type="password"
                    />
                  </Grid>
                </>
              )}

              {/* Optional Plan Selection Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Subscription Plan (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Optionally assign a subscription plan to this organization during creation.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                {plansLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Select Plan</InputLabel>
                      <Select
                        value={selectedPlanId}
                        onChange={(e) => {
                          setSelectedPlanId(e.target.value);
                          // Reset trial settings when plan changes
                          if (!e.target.value) {
                            setIsTrial(false);
                            setTrialDays(14);
                          }
                        }}
                        label="Select Plan"
                      >
                        <MenuItem value="">
                          <em>No plan (create without subscription)</em>
                        </MenuItem>
                        {plans
                          ?.filter((plan) => plan.isActive)
                          .map((plan) => (
                            <MenuItem key={plan.id} value={plan.id}>
                              {plan.name} - {formatPrice(plan.basePrice, plan.currency)}{' '}
                              {formatBillingInterval(plan.billingInterval)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>

                    {/* Plan Details */}
                    {selectedPlan && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Plan Details
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {selectedPlan.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Price:</strong> {formatPrice(selectedPlan.basePrice, selectedPlan.currency)}{' '}
                          {formatBillingInterval(selectedPlan.billingInterval)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Max Users:</strong>{' '}
                          {selectedPlan.maxUsers || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Max Stores:</strong>{' '}
                          {selectedPlan.maxStores || 'Unlimited'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Max Warehouses:</strong>{' '}
                          {selectedPlan.maxWarehouses || 'Unlimited'}
                        </Typography>
                      </Box>
                    )}

                    {/* Trial/Paid Toggle - only shown after selecting a plan */}
                    {selectedPlan && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Subscription Type
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isTrial}
                              onChange={(e) => setIsTrial(e.target.checked)}
                              color="info"
                            />
                          }
                          label={isTrial ? 'Trial Period' : 'Paid Subscription'}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {isTrial
                            ? 'Start a free trial period for this organization'
                            : 'Assign as a paid subscription (billing starts immediately)'}
                        </Typography>

                        {/* Trial Days Input - only shown when trial is ON */}
                        {isTrial && (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                            <TextField
                              fullWidth
                              type="number"
                              label="Trial Duration (days)"
                              value={trialDays}
                              onChange={(e) => setTrialDays(Number(e.target.value))}
                              inputProps={{ min: 1, max: 90 }}
                              helperText={`Trial will expire on ${trialExpirationDate.toLocaleDateString()}`}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box display="flex" gap={2} mt={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={isPending}
                  >
                    {isPending ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => navigate('/organizations')}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
